import { showNotification } from '@helpers/notification-helper';
import { useAppSelector } from '@hooks/app/redux';
import { memberApiV2 } from '@lib/api';
import { Button, Drawer, Form, Select, TextField } from '@ui';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import * as Yup from 'yup';

export type TFormValues = {
  name: string;
  surname: string;
  phone: string;
};

type TProps = {
  staffData: {
    role: {
      id: string;
    };
    user: {
      id: string;
      email: string;
    };
  } | null;
  onRequestClose: () => void;
  visible: boolean;
};

const AddEditStaff: React.FC<TProps> = ({ staffData, onRequestClose, visible }) => {
  const { formatMessage } = useIntl();
  const providerId = useAppSelector((state) => state.users.currentProvider?.id)!;

  // Load available roles
  const { isLoading: isLoadingRoles, data: roles } = useQuery(
    ['business-roles', providerId],
    async () => {
      const { data: axData } = await memberApiV2.getRoles(providerId as string);
      return axData.data;
    },
    { enabled: visible },
  );

  const roleOptions = React.useMemo(
    () =>
      roles?.map((item: any) => ({
        value: item.id,
        label: formatMessage({ id: `business_staff_role_${item.name}` }),
        isDisabled: !item.selectable,
      })) || [],
    [roles],
  );

  const initialValues = React.useMemo(() => {
    if (!roles || !staffData) {
      return {
        email: '',
        phone: '',
        role: null,
      };
    }

    const selectedRole = roleOptions.find((r: any) => r.value === staffData?.role.id);

    return {
      email: staffData.user.email,
      role: selectedRole,
    };
  }, [staffData?.role.id, roleOptions]);

  const [, formOptions] = Form.useForm({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object().shape({
      email: !staffData
        ? Yup.string()
            .required(formatMessage({ id: 'validation.required' }))
            .email(formatMessage({ id: 'validation.email' }))
        : Yup.string(),
      role: Yup.mixed().required(formatMessage({ id: 'validation.required' })),
    }),
    onSubmit: async (values) => {
      try {
        if (!staffData) {
          // invite
          await memberApiV2.inviteStaff(providerId, {
            email: values.email,
            role: values.role?.value,
          });
        } else {
          await memberApiV2.changeRoleStaff(providerId, staffData.user.id, values.role?.value);
        }

        onRequestClose();

        showNotification({
          type: 'SUCCESS',
          title: formatMessage({
            id: !staffData ? 'staff_modal_add_success' : 'staff_modal_edit_success',
          }),
          copy: '',
        });
      } catch (err) {
        showNotification({
          type: 'ERROR',
          title: 'Nie je možné pridať rovnakého zamestnanca',
          copy: '',
        });
      }
    },
  });

  if (isLoadingRoles) {
    return null;
  }

  return (
    <Drawer
      visible={visible}
      onClose={onRequestClose}
      title={
        <FormattedMessage id={staffData ? 'staff_modal_edit_heading' : 'staff_modal_add_heading'} />
      }
      destroyOnClose
    >
      <Form {...formOptions}>
        <Form.Field
          label={formatMessage({ id: 'staff_modal_email' })}
          required
          name="email"
          disabled={!!staffData}
        >
          <TextField style={{ width: '100%' }} />
        </Form.Field>

        <Form.Field
          label={formatMessage({ id: 'staff_modal_role' })}
          required
          name="role"
          getValueFromEvent={(e) => e}
        >
          <Select options={roleOptions} />
        </Form.Field>
        <Form.Custom>
          {(f) => (
            <>
              <Button loading={f.isSubmitting} type="submit" style={{ marginTop: 16 }}>
                {formatMessage({ id: 'continue_btn_label' })}
              </Button>
            </>
          )}
        </Form.Custom>
      </Form>
    </Drawer>
  );
};

export default AddEditStaff;
